import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import categoryService from '../../../services/categoryService';
import storeService from '../../../services/storeService';
import { useStore } from '../../../context/StoreContext';
import ImageUploader from '../../common/ImageUploader';
import { useCurrency } from '../../../hooks/useCurrency';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const { currentStore, isSuperAdmin } = useStore();

  const [loading, setLoading] = useState(false);
  const [availableStores, setAvailableStores] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [productData, setProductData] = useState(null); // For displaying auto-calculated basePrice
  const [galleryImages, setGalleryImages] = useState([]);
  // Add mode: the product doesn't exist yet, so the chosen main image is kept here
  // and uploaded right after the product is created.
  const [pendingMainImage, setPendingMainImage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    mainImageUrl: '',
    sku: '',
    // basePrice removed - now auto-calculated from variants
    storeId: currentStore?.id || '',
    isActive: true,
    sellOnline: true,
    metaTitle: '',
    metaDescription: '',
    primaryCategoryId: '',
  });

  useEffect(() => {
    if (isSuperAdmin) {
      fetchStores();
    }
    if (isEdit) {
      fetchProduct();
    } else if (formData.storeId) {
      fetchCategoriesForStore(formData.storeId);
    }
  }, [id, isEdit]);

  const fetchStores = async () => {
    try {
      const data = await storeService.getAllStores();
      setAvailableStores(data);
    } catch (error) {
      toast.error(t('stores.fetchError'));
      console.error(error);
    }
  };

  const fetchCategoriesForStore = async (storeId) => {
    try {
      const data = await categoryService.getAllCategories();
      setAvailableCategories(data);
    } catch (error) {
      toast.error(t('categories.fetchError'));
      console.error(error);
    }
  };

  const fetchProduct = async () => {
    try {
      const data = await productService.getProductById(id);
      setProductData(data);
      setGalleryImages(data.galleryImages || []);
      setFormData({
        name: data.name,
        slug: data.slug || '',
        description: data.description || '',
        mainImageUrl: data.mainImageUrl || '',
        sku: data.sku,
        // basePrice removed - displayed separately as read-only
        storeId: data.storeId,
        isActive: data.isActive,
        sellOnline: data.sellOnline !== false,
        metaTitle: data.metaTitle || '',
        metaDescription: data.metaDescription || '',
        primaryCategoryId: data.primaryCategory?.id || '',
      });

      // Set selected categories
      if (data.categories) {
        const categoryObjects = data.categories.map(cat => ({
          id: cat.id,
          name: cat.name,
        }));
        setSelectedCategories(categoryObjects);
      }

      if (data.storeId) {
        fetchCategoriesForStore(data.storeId);
      }
    } catch (error) {
      toast.error(t('products.fetchError'));
      console.error(error);
    }
  };

  const handleStoreChange = (storeId) => {
    setFormData({ ...formData, storeId });
    setSelectedCategories([]);
    setFormData(prev => ({ ...prev, primaryCategoryId: '' }));
    if (storeId) {
      fetchCategoriesForStore(storeId);
    }
  };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (selectedCategories.length === 0) {
            toast.error(t('products.categories') + ' ' + t('common.add'));
            return;
        }

        if (!formData.primaryCategoryId) {
            toast.error(t('products.categories'));
            return;
        }

        setLoading(true);
        try {
            const productData = {
                name: formData.name,
                slug: formData.slug.trim() === '' ? undefined : formData.slug.trim(),
                description: formData.description,
                mainImageUrl: formData.mainImageUrl,
                storeId: formData.storeId,
                sku: formData.sku.trim() === '' ? undefined : formData.sku.trim(),
                // basePrice removed - auto-calculated from variants
                isActive: formData.isActive,
                sellOnline: formData.sellOnline,
                metaTitle: formData.metaTitle,
                metaDescription: formData.metaDescription,
                categoryIds: selectedCategories.map(cat => cat.id),
                primaryCategoryId: formData.primaryCategoryId,
            };

            console.log('📤 Sending product data:', productData); // ← Debug log

            if (isEdit) {
                await productService.updateProduct(id, productData);
                toast.success(t('products.updateSuccess'));
            } else {
                const response = await productService.createProduct(productData);
                console.log('✅ Product created:', response); // ← Debug log
                const createdId = response?.id ?? response?.data?.id;
                if (pendingMainImage && createdId) {
                    try {
                        await productService.uploadMainImage(createdId, pendingMainImage);
                    } catch (uploadError) {
                        toast.warning(uploadError.response?.data?.message || t('common.saveError'));
                    }
                }
                toast.success(t('products.createSuccess'));
            }
            navigate('/products');
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
            console.error('❌ Error:', error);
        } finally {
            setLoading(false);
        }
    };
    const generateSkuPreview = () => {
        if (!formData.name || formData.name.trim() === '') return '';
        if (formData.sku && formData.sku.trim() !== '') return formData.sku; // User provided SKU
        
        // Get store code
        let storeCode = '';
        if (isSuperAdmin && formData.storeId) {
            const selectedStore = availableStores.find(s => s.id === formData.storeId);
            storeCode = selectedStore?.code || '';
        } else if (currentStore) {
            storeCode = currentStore.code;
        }
        
        if (!storeCode) return '';
        
        // Generate preview
        const productSlug = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
        
        return `${storeCode}-${productSlug}`;
    };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? t('products.editProduct') : t('products.createProduct')}
      </Typography>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
                
                {/* Show preview if SKU is empty and name is filled */}
                {formData.sku.trim() === '' && formData.name.trim() !== '' && formData.storeId && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            <strong>{t('products_ext.skuPreview')}</strong> {generateSkuPreview()}
                        </Alert>
                    </Grid>
                )}
                {/* Product Name */}
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label={t('products.product')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />
              </Grid>

              {/* SKU */}
              <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label={t('products_ext.skuOptional')}
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    helperText={t('products_ext.skuHelper')}
                    placeholder={t('products_ext.skuPlaceholder')}
                />
              </Grid>

              {/* Slug */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('products_ext.slugLabel')}
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  helperText={t('products_ext.slugHelper')}
                  placeholder={t('products_ext.slugPlaceholder')}
                />
              </Grid>

              {/* Base Price - Auto-calculated, Read-only */}
              {isEdit && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('products_ext.basePriceAuto')}
                    value={productData?.basePrice ? formatMoney(productData.basePrice) : t('products_ext.noVariantsYet')}
                    disabled
                    helperText={t('products_ext.basePriceHelper')}
                  />
                </Grid>
              )}
              {!isEdit && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t('products_ext.basePriceInfo')}
                  </Alert>
                </Grid>
              )}

              {/* Store Assignment */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('roles.store')}
                  value={formData.storeId}
                  onChange={(e) => handleStoreChange(e.target.value)}
                  required
                  disabled={!isSuperAdmin || isEdit}
                >
                  {isSuperAdmin ? (
                    availableStores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </MenuItem>
                    ))
                  ) : (
                    currentStore && (
                      <MenuItem value={currentStore.id}>
                        {currentStore.name} ({currentStore.code})
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>

              {/* Main Image */}
              <Grid item xs={12} md={6}>
                {isEdit ? (
                  <ImageUploader
                    label={t('products_ext.mainImageLabel')}
                    currentUrl={formData.mainImageUrl}
                    onUpload={async (file) => {
                      const { url } = await productService.uploadMainImage(id, file);
                      setFormData((prev) => ({ ...prev, mainImageUrl: url }));
                      toast.success(t('products_ext.mainImageUpdated'));
                    }}
                    onDelete={async () => {
                      await productService.deleteMainImage(id);
                      setFormData((prev) => ({ ...prev, mainImageUrl: '' }));
                      toast.success(t('products_ext.mainImageRemoved'));
                    }}
                    size={180}
                  />
                ) : (
                  <ImageUploader
                    label={t('products_ext.mainImageLabel')}
                    currentUrl={null}
                    onUpload={async (file) => { setPendingMainImage(file); }}
                    onDelete={async () => { setPendingMainImage(null); }}
                    size={180}
                  />
                )}
              </Grid>

              {/* Gallery Images — edit mode only */}
              {isEdit && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('products_ext.galleryImagesLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                    {galleryImages.map((url) => (
                      <Box key={url} sx={{ position: 'relative' }}>
                        <img
                          src={url}
                          alt="gallery"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            '&:hover': { bgcolor: 'white' },
                          }}
                          onClick={async () => {
                            try {
                              await productService.deleteGalleryImage(id, url);
                              setGalleryImages((prev) => prev.filter((u) => u !== url));
                              toast.success(t('products_ext.galleryImageRemoved'));
                            } catch {
                              toast.error(t('products_ext.galleryImageRemoveError'));
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {galleryImages.length < 2 && (
                      <ImageUploader
                        currentUrl={null}
                        onUpload={async (file) => {
                          const { url } = await productService.uploadGalleryImage(id, file);
                          setGalleryImages((prev) => [...prev, url]);
                          toast.success(t('products_ext.galleryImageAdded'));
                        }}
                        size={120}
                      />
                    )}
                  </Box>
                </Grid>
              )}

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={4}
                />
              </Grid>

              {/* Categories */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={availableCategories}
                  getOptionLabel={(option) => option.name}
                  value={selectedCategories}
                  onChange={(event, newValue) => setSelectedCategories(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('products.categories')}
                      placeholder={t('products_ext.selectCategoriesPlaceholder')}
                      required={selectedCategories.length === 0}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        color={option.id === formData.primaryCategoryId ? 'primary' : 'default'}
                      />
                    ))
                  }
                />
              </Grid>

              {/* Primary Category */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('products_ext.primaryCategory')}
                  value={formData.primaryCategoryId}
                  onChange={(e) => setFormData({ ...formData, primaryCategoryId: e.target.value })}
                  required
                  disabled={selectedCategories.length === 0}
                  helperText={t('products_ext.primaryCategoryHelper')}
                >
                  <MenuItem value="">
                    <em>{t('products_ext.selectPrimaryCategory')}</em>
                  </MenuItem>
                  {selectedCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Meta Title (SEO) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('products.metaTitle')}
                  value={formData.metaTitle}
                  onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                  helperText={t('products_ext.metaTitleHelper')}
                />
              </Grid>

              {/* Meta Description (SEO) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('products.metaDescription')}
                  value={formData.metaDescription}
                  onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                  multiline
                  rows={2}
                  helperText={t('products_ext.metaDescriptionHelper')}
                />
              </Grid>

              {/* Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              {/* Sell on the website */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sellOnline}
                      onChange={(e) => setFormData({ ...formData, sellOnline: e.target.checked })}
                    />
                  }
                  label={t('sellOnline.label')}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('sellOnline.helpProduct')}
                </Typography>
              </Grid>

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/products')} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.save') + '...' : isEdit ? t('products.editProduct') : t('products.createProduct')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductForm;